/**
 * Editorial copy for the master detail screen — the strategy blurb, the risk
 * tier and the badge beside the name. None of it exists on TradeSync: it's
 * marketing copy POW writes per strategy, so it lives here, keyed by master
 * name (matched case- and space-insensitively so "Recovery Signal" and
 * "RECOVERY SIGNAL" are the same entry).
 *
 * `about` and `badge` are fallbacks only. The detail screen prefers what was
 * typed on the admin master form — accounts.master_description and
 * accounts.master_strategy, sent as `description` and `strategy` on the master
 * payloads — and drops to the copy here when those are blank, so rewording is
 * normally done in the portal rather than in this file.
 *
 * `risk` has no field behind it and is still edited here. A master with no entry
 * falls back to DEFAULT_PROFILE: no risk pill, a generic badge, and an About
 * card only if it has a description of its own.
 */

const DEFAULT_PROFILE = {
  badge: 'Third-party strategy',
  risk: null,
  about: null,
};

// Risk must read as one of the three tiers the pill knows how to colour:
// "Low", "Medium" or "High".
const PROFILES = {
  // The one first-party strategy, and the only entry that has to carry its own
  // blurb: the trade copier is the POW Index page now, so it renders under this
  // name before the desk has created the master account there is normally a
  // description on. Risk stays null — the tier is the account's to state, not
  // something to assert on a master that doesn't exist yet.
  'pow index': {
    badge: 'POW strategy',
    risk: null,
    about:
      'A professionally curated portfolio of select POW strategies, dynamically ' +
      'combined to diversify risk and optimize performance.',
  },
  apex: {
    badge: 'Third-party strategy · Institutional grade',
    risk: 'Medium',
    about:
      'Apex is an institutional FX & US30 strategy designed by Ben Palmer, Head of FX Trading ' +
      'at Xnumia Management. Built from over a decade of institutional trading experience, it ' +
      'combines order flow, market microstructure, and contrarian positioning to deliver ' +
      'disciplined, low drawdown, rules-based execution.',
  },
  bullion: {
    badge: 'Third-party strategy',
    risk: null,
    about:
      'John Ward is the founder and the architect of the Bullion System. With years dedicated ' +
      'exclusively to trading XAUUSD, he has developed a systematic strategy that combines ' +
      'institutional order flow, session-based execution, and disciplined risk management to ' +
      'deliver consistent, rules-based performance.',
  },
  'fox-3i': {
    badge: 'Third-party strategy',
    risk: null,
    about:
      'Gerard Iwema is the founder and CEO of FoX-i Systematic Market Intelligence and the ' +
      'architect of the FoX-3i System. Developed alongside Technology Officer Bernie Schicht, ' +
      'the strategy combines systematic trend-following, multi-timeframe analysis, and ' +
      'disciplined risk management to deliver consistent, rules-based execution across major ' +
      'FX markets.',
  },
  'suits-t': {
    badge: 'Third-party strategy',
    risk: null,
    about:
      'Sergio Vargas is the operator of the Suits-T System and a Darwinex-Verified Trader. ' +
      'Developed through years of trading US equity indices, the strategy combines structural ' +
      'price analysis, range-based execution, and disciplined risk management to deliver ' +
      'consistent, rules-based performance across the Dow Jones, S&P 500, and NASDAQ.',
  },
  atlas: {
    badge: 'In-house strategy',
    risk: null,
    about:
      'Atlas is an in-house POW trading system. The strategy applies a rules-based mean ' +
      'reversion methodology across a diversified portfolio of FX pairs, using exposure filters ' +
      'to manage currency concentration while combining disciplined risk management with ' +
      'systematic profit-taking for consistent, repeatable performance.',
  },
  foundry: {
    badge: 'In-house strategy',
    risk: null,
    about:
      'Foundry is an in-house POW trading system. Running the same proven core methodology as ' +
      'Atlas across a different selection of FX pairs, the strategy adds diversification at the ' +
      'system level while using statistical price extremes, disciplined risk controls, and ' +
      'systematic trade management to deliver consistent, rules-based execution.',
  },
  recovery: {
    badge: 'In-house strategy',
    risk: 'Low',
    about:
      'Recovery is an in-house POW trading system. Developed as the lowest-risk version of the ' +
      'Atlas methodology, it applies the same systematic FX framework with reduced position ' +
      'sizing, prioritizing capital preservation and steady account recovery while maintaining ' +
      'disciplined, rules-based execution.',
  },
};

const keyOf = (name) => String(name || '').trim().toLowerCase().replace(/\s+/g, ' ');

export function profileFor(name) {
  return PROFILES[keyOf(name)] || DEFAULT_PROFILE;
}

export default PROFILES;
